import axios from 'axios';

export const crmService = {
  async getLeads({ page = 1, limit = 50, search = '' } = {}) {
    try {
      const params = { page, limit };
      if (search && search.trim()) {
        params.search = search.trim();
      }
      const response = await axios.get('/crm/leads', {
        params,
      });
      return response.data; // Returns { leads: [], pagination: {} }
    } catch (error) {
      console.error('Error fetching leads:', error);
      throw error;
    }
  },

  async getLeadById(id) {
    try {
      const response = await axios.get(`/crm/leads/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching lead:', error);
      throw error;
    }
  },

  async createLead(leadData) {
    try {
      const response = await axios.post('/crm/leads', leadData);
      return {
        ...response.data,
        statusCode: response.status,
      };
    } catch (error) {
      // For 409 Conflict, return the existing lead data
      if (error.response?.status === 409) {
        return {
          ...error.response.data.existingLead,
          statusCode: 409,
        };
      }
      console.error('Error creating lead:', error);
      throw error;
    }
  },

  async updateLead(id, leadData) {
    try {
      const response = await axios.patch(`/crm/leads/${id}`, leadData);
      return response.data;
    } catch (error) {
      console.error('Error updating lead:', error);
      throw error;
    }
  },

  async deleteLead(id) {
    try {
      const response = await axios.delete(`/crm/leads/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting lead:', error);
      throw error;
    }
  },

  async archiveLead(id) {
    try {
      const response = await axios.post(`/crm/leads/${id}/archive`);
      return response.data;
    } catch (error) {
      console.error('Error archiving lead:', error);
      throw error;
    }
  },

  async restoreLead(id) {
    try {
      const response = await axios.post(`/crm/leads/${id}/restore`);
      return response.data;
    } catch (error) {
      console.error('Error restoring lead:', error);
      throw error;
    }
  },

  async addLeadActivity(leadId, activity) {
    try {
      const response = await axios.post(`/crm/leads/${leadId}/activities`, activity);
      return response.data;
    } catch (error) {
      console.error('Error adding lead activity:', error);
      throw error;
    }
  },

  async getLeadSubmissions(leadId, { page = 1, limit = 20 } = {}) {
    try {
      const response = await axios.get(`/crm/leads/${leadId}/submissions`, {
        params: { page, limit },
      });
      return response.data; // Returns { submissions: [], pagination: {} }
    } catch (error) {
      console.error('Error fetching lead submissions:', error);
      throw error;
    }
  },

  async getLeadAppointments(leadId, { page = 1, limit = 20 } = {}) {
    try {
      const response = await axios.get(`/crm/leads/${leadId}/appointments`, {
        params: { page, limit },
      });
      return response.data; // Returns { appointments: [], pagination: {} }
    } catch (error) {
      console.error('Error fetching lead appointments:', error);
      throw error;
    }
  },
  async getOrganizationProposals(params = {}) {
    try {
      const { page, limit, ...otherParams } = params;
      const response = await axios.get(`/crm/proposals`, {
        params: { page, limit, ...otherParams },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching proposals:', error);
      throw error;
    }
  },

  /**
   * Get proposal statistics for the organization
   * @param {Object} params - Query parameters
   * @param {string} params.period - Period filter: '30d', '90d', 'ytd', 'all'
   * @param {boolean} params.compare - Include comparison with previous period
   * @returns {Promise<Object>} Stats object with current, previous, trends, and year_to_date
   */
  async getProposalStats(params = {}) {
    try {
      const response = await axios.get(`/crm/proposals/stats`, {
        params,
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching proposal stats:', error);
      throw error;
    }
  },

  async renderProposalTemplates(proposal) {
    const response = await axios.post('/crm/proposals/render-templates', { proposal });
    return response.data;
  },

  async getProposals(leadId, params = {}) {
    try {
      const { page, limit, ...otherParams } = params;
      const response = await axios.get(`/crm/leads/${leadId}/proposals`, {
        params: { page, limit, ...otherParams },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching proposals:', error);
      throw error;
    }
  },

  async getDraftProposal(leadId, proposalId) {
    try {
      const response = await axios.get(`/crm/leads/${leadId}/proposals/${proposalId}/draft`);
      return response.data;
    } catch (error) {
      console.error('Error fetching proposals:', error);
      throw error;
    }
  },
  async getProposal(leadId, proposalId) {
    try {
      const response = await axios.get(`/crm/leads/${leadId}/proposals/${proposalId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching proposals:', error);
      throw error;
    }
  },
  async getProposalById(proposalId) {
    try {
      const response = await axios.get(`/crm/proposals/${proposalId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching proposal:', error);
      throw error;
    }
  },
  async updateProposal(leadId, proposalId, proposal) {
    try {
      const response = await axios.patch(`/crm/leads/${leadId}/proposals/${proposalId}`, proposal);
      return response.data;
    } catch (error) {
      console.error('Error saving proposal:', error);
      throw error;
    }
  },
  async createProposal(leadId, proposal) {
    try {
      const response = await axios.post(`/crm/leads/${leadId}/proposals`, proposal);
      return response.data;
    } catch (error) {
      console.error('Error creating proposal:', error);
      throw error;
    }
  },
  async duplicateProposal(proposalId, leadId = null) {
    try {
      const payload = leadId ? { lead_id: leadId } : {};
      const response = await axios.post(`/crm/proposals/${proposalId}/duplicate`, payload);
      return response.data;
    } catch (error) {
      console.error('Error duplicating proposal:', error);
      throw error;
    }
  },
  /**
   * Send a proposal via email (async).
   *
   * Backend returns `202 Accepted` with `{ proposal, job_id, queued_recipients }`.
   * The proposal will be in `status='sending'` until the worker has finished;
   * callers should poll via `pollProposalSendStatus` (or `getProposal`) and
   * watch for `sent` (success) or `draft` (failure — worker reverted).
   *
   * For the legacy synchronous fallback path (no html/css forwarded) the
   * backend returns the same wrapper shape with `job_id: null`, so callers
   * can rely on `.proposal` being present in either branch.
   *
   * @returns {Promise<{ status: number, proposal: object, jobId: string|null, recipients: string[] }>}
   */
  /**
   * Exact email + attachment list the customer would receive. Nothing is sent.
   */
  async previewProposalSend(leadId, proposalId, emailContent) {
    const response = await axios.post(`/crm/leads/${leadId}/proposals/${proposalId}/send-preview`, {
      recipients: emailContent.recipients,
      subject: emailContent.emailSubject,
      body: emailContent.emailContent,
      attachments: emailContent.attachments || [],
      excluded_attachments: emailContent.excludedAttachments || [],
    });
    return response.data;
  },

  async sendProposal(leadId, proposalId, emailContent) {
    try {
      const response = await axios.post(`/crm/leads/${leadId}/proposals/${proposalId}/send`, {
        recipients: emailContent.recipients,
        subject: emailContent.emailSubject,
        body: emailContent.emailContent,
        ...(emailContent.attachments !== undefined
          ? { attachments: emailContent.attachments }
          : {}),
        ...(emailContent.excludedAttachments !== undefined
          ? { excluded_attachments: emailContent.excludedAttachments }
          : {}),
      });
      const data = response.data || {};
      return {
        status: response.status,
        proposal: data.proposal || null,
        jobId: data.job_id ?? null,
        recipients: data.queued_recipients || [],
        raw: data,
      };
    } catch (error) {
      console.error('Error sending proposal:', error);
      throw error;
    }
  },

  async bulkSendProposals(proposalIds) {
    try {
      const response = await axios.post('/crm/proposals/send', { proposalIds });
      return response.data;
    } catch (error) {
      console.error('Error bulk sending proposal:', error);
      throw error;
    }
  },

  /**
   * Poll `GET /api/proposals/:id` until the worker has finished sending.
   *
   * Resolves with `{ result, proposal }`:
   *  - `'sent'`      → worker succeeded (status flipped to `sent`)
   *  - `'failed'`    → worker reverted to `draft`
   *  - `'timed_out'` → did not see a terminal state within `timeoutMs`
   *
   * @param {string} leadId
   * @param {string} proposalId
   * @param {Object} [opts]
   * @param {(p: any) => void} [opts.onUpdate]
   * @param {number} [opts.intervalMs=2000]
   * @param {number} [opts.timeoutMs=60000]
   */
  async pollProposalSendStatus(
    leadId,
    proposalId,
    { onUpdate, intervalMs = 2000, timeoutMs = 60000 } = {}
  ) {
    const start = Date.now();
    while (true) {
      const fresh = await this.getProposal(leadId, proposalId);
      if (typeof onUpdate === 'function') onUpdate(fresh);

      if (fresh?.status === 'sent') {
        return { result: 'sent', proposal: fresh };
      }
      if (fresh?.status === 'draft') {
        return { result: 'failed', proposal: fresh };
      }

      if (Date.now() - start > timeoutMs) {
        return { result: 'timed_out', proposal: fresh };
      }

      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
  },
  async bulkSendStatus(proposalIds) {
    const response = await axios.post('/crm/proposals/send-status', { proposalIds });
    return response.data?.statuses || [];
  },

  /**
   * Poll `POST /crm/proposals/send-status` for a batch of proposal ids until every
   * one reaches a terminal state, or timeoutMs elapses.
   *
   * Resolves with a Map<id, 'sent' | 'failed' | 'withdrawn' | 'timed_out'>:
   *  - `'sent'`      → worker succeeded
   *  - `'failed'`    → worker reverted to draft
   *  - `'withdrawn'` → a deal-sibling, superseded as a side effect of another send
   *  - `'timed_out'` → still not terminal when the timeout was hit
   *
   * Default timeout scales with batch size — the queue retries up to 5 times with
   * exponential backoff up to ~2 minutes cumulative (see queues/index.js), so a
   * fixed short window would falsely report "timed out" on any real batch.
   *
   * @param {string[]} proposalIds
   * @param {Object} [opts]
   * @param {(settled: Map<string,string>) => void} [opts.onUpdate]
   * @param {number} [opts.intervalMs=3000]
   * @param {number} [opts.timeoutMs]
   */
  async pollBulkSendStatus(proposalIds, { onUpdate, intervalMs = 3000, timeoutMs } = {}) {
    const effectiveTimeout = timeoutMs ?? Math.max(60000, proposalIds.length * 60000);
    const start = Date.now();
    const pending = new Set(proposalIds);
    const settled = new Map();

    while (pending.size > 0 && Date.now() - start <= effectiveTimeout) {
      const statuses = await this.bulkSendStatus(Array.from(pending));
      for (const { id, status } of statuses) {
        if (status === 'sent') {
          settled.set(id, 'sent');
          pending.delete(id);
        } else if (status === 'draft') {
          settled.set(id, 'failed');
          pending.delete(id);
        } else if (status === 'withdrawn') {
          settled.set(id, 'withdrawn');
          pending.delete(id);
        }
      }
      if (typeof onUpdate === 'function') onUpdate(settled);
      if (pending.size === 0) break;
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }

    for (const id of pending) settled.set(id, 'timed_out');
    return settled;
  },
  async deleteProposal(leadId, proposalId) {
    try {
      const response = await axios.delete(`/crm/leads/${leadId}/proposals/${proposalId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting proposal:', error);
      throw error;
    }
  },

  async withdrawProposal(leadId, proposalId, reason = null) {
    try {
      const response = await axios.post(
        `/crm/leads/${leadId}/proposals/${proposalId}/withdraw`,
        reason ? { reason } : {}
      );
      return response.data;
    } catch (error) {
      console.error('Error withdrawing proposal:', error);
      throw error;
    }
  },

  async rejectProposal(leadId, proposalId, reason = null, reasonCode = null) {
    try {
      const body = {};
      if (reason) body.reason = reason;
      if (reasonCode) body.reason_code = reasonCode;
      const response = await axios.post(
        `/crm/leads/${leadId}/proposals/${proposalId}/reject`,
        body
      );
      return response.data;
    } catch (error) {
      console.error('Error rejecting proposal:', error);
      throw error;
    }
  },

  async restoreProposal(leadId, proposalId) {
    try {
      const response = await axios.post(`/crm/leads/${leadId}/proposals/${proposalId}/restore`);
      return response.data;
    } catch (error) {
      console.error('Error restoring proposal:', error);
      throw error;
    }
  },

  async restoreProposalToSent(leadId, proposalId) {
    try {
      const response = await axios.post(
        `/crm/leads/${leadId}/proposals/${proposalId}/restore-to-sent`
      );
      return response.data;
    } catch (error) {
      console.error('Error restoring proposal to sent:', error);
      throw error;
    }
  },

  async markProposalAsAccepted(leadId, proposalId, reason = null) {
    try {
      const response = await axios.post(
        `/crm/leads/${leadId}/proposals/${proposalId}/mark-accepted`,
        reason ? { reason } : {}
      );
      return response.data;
    } catch (error) {
      console.error('Error marking proposal as accepted:', error);
      throw error;
    }
  },

  async markProposalAsRejected(leadId, proposalId, reason = null, reasonCode = null) {
    try {
      const body = {};
      if (reason) body.reason = reason;
      if (reasonCode) body.reason_code = reasonCode;
      const response = await axios.post(
        `/crm/leads/${leadId}/proposals/${proposalId}/mark-rejected`,
        body
      );
      return response.data;
    } catch (error) {
      console.error('Error marking proposal as rejected:', error);
      throw error;
    }
  },

  async markProposalAsSent(leadId, proposalId) {
    try {
      const response = await axios.post(`/crm/leads/${leadId}/proposals/${proposalId}/mark-sent`);
      return response.data;
    } catch (error) {
      console.error('Error marking proposal as sent:', error);
      throw error;
    }
  },

  async bulkDeleteProposals(leadId, proposalIds) {
    try {
      const response = await axios.delete(`/crm/leads/${leadId}/proposals`, {
        data: { proposalIds },
      });
      return response.data;
    } catch (error) {
      console.error('Error bulk deleting proposals:', error);
      throw error;
    }
  },

  async downloadProposalPDF(proposalId, proposalData, { bare = false } = {}) {
    try {
      const response = await axios.post(
        `/proposals/${proposalId}/pdf`,
        {
          proposal: proposalData,
          ...(bare ? { bare: true } : {}),
        },
        {
          responseType: 'blob',
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error downloading proposal PDF:', error);
      throw error;
    }
  },

  // === Submission Revision Methods ===

  /**
   * Get the revision URL for a submission
   * Opens the FormView with pre-filled data for editing.
   *
   * @param {string} submissionId
   * @param {string} returnUrl
   * @param {string|null} [intent] Optional revision intent that the backend
   *   bakes into the returned revise_url (e.g. 'create_definitive_from_proforma').
   *   When omitted the backend defaults to a regular 'revise' intent.
   */
  async getSubmissionReviseUrl(submissionId, returnUrl, intent = null) {
    try {
      const params = { return_url: returnUrl };
      if (intent) params.intent = intent;
      const response = await axios.get(`submissions/${submissionId}/revise-url`, {
        params,
      });
      return response.data;
    } catch (error) {
      console.error('Error getting submission revise URL:', error);
      throw error;
    }
  },

  /**
   * Create a proposal from an existing submission
   * Used when autoCreateProposal is disabled
   */
  async createProposalFromSubmission(submissionId) {
    try {
      const response = await axios.post(`/submissions/${submissionId}/create-proposal`);
      return response.data;
    } catch (error) {
      console.error('Error creating proposal from submission:', error);
      throw error;
    }
  },

  /**
   * Get the revision URL for a proposal
   * Finds the source submission and opens FormView.
   *
   * @param {string} proposalId
   * @param {string} returnUrl
   * @param {string|null} [intent] Optional revision intent that the backend
   *   bakes into the returned revise_url (e.g. 'create_definitive_from_proforma').
   *   When omitted the backend defaults to a regular 'revise' intent.
   */
  async getProposalReviseUrl(proposalId, returnUrl, intent = null) {
    try {
      const params = { return_url: returnUrl };
      if (intent) params.intent = intent;
      const response = await axios.get(`/crm/proposals/${proposalId}/revise-url`, {
        params,
      });
      return response.data;
    } catch (error) {
      console.error('Error getting proposal revise URL:', error);
      throw error;
    }
  },

  async resetProposalNumbers() {
    try {
      const response = await axios.post('/crm/proposals/reset-numbers');
      return response.data;
    } catch (error) {
      console.error('Error resetting proposal numbers:', error);
      throw error;
    }
  },
};
